import { useEffect, useState } from "react"
import AdminLayout from "@/components/admin/AdminLayout"
import { getAdminReviews, deleteAdminReview } from "@/api/reviewApi"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"

export default function AdminReviewPage() {
    const [reviews, setReviews] = useState<any[]>([])
    const [loading, setLoading] = useState(false)
    const [selectedReview, setSelectedReview] = useState<any>(null)

    const fetchReviews = async () => {
        try {
            setLoading(true)
            const response = await getAdminReviews()
            console.log('review list : ', response);
            setReviews(response.data || [])
        } catch (error) {
            console.error("리뷰 목록 조회 실패:", error)
            alert("리뷰 목록을 불러오지 못했습니다.")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchReviews()
    }, [])

    const handleDelete = async (e: React.MouseEvent, reviewId: number, isDeleted: string) => {
        e.stopPropagation()

        // 💡 이미 숨김 처리된 리뷰라면 작동하지 않도록 방어 로직 추가
        if (isDeleted === 'Y') {
            alert("이미 숨김 처리된 리뷰입니다.")
            return
        }

        if (!confirm("이 리뷰를 숨김 처리하시겠습니까?\n(고객 화면에서 노출되지 않습니다.)")) return

        try {
            await deleteAdminReview(reviewId)
            alert("리뷰가 숨김 처리되었습니다.")
            fetchReviews()
            setSelectedReview(null)
        } catch (error) {
            console.error("리뷰 처리 실패:", error)
            alert("리뷰 처리에 실패했습니다.")
        }
    }

    const renderStars = (rating: number) => {
        return (
            <div className="flex text-yellow-400 text-sm">
                {[1, 2, 3, 4, 5].map((star) => (
                    <span key={star} className={star <= rating ? "" : "text-slate-200"}>★</span>
                ))}
            </div>
        )
    }

    return (
        <AdminLayout>
            <div className="space-y-6">
                <div>
                    <h2 className="text-3xl font-bold">리뷰 관리</h2>
                    <p className="mt-2 text-sm text-slate-500">고객들이 작성한 상품 리뷰를 확인하고 숨김 처리할 수 있습니다.</p>
                </div>

                <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                    <CardContent className="p-0">
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[980px] text-sm text-left">
                                <thead>
                                <tr className="border-b border-slate-200 bg-slate-50 text-slate-500">
                                    <th className="px-4 py-4 font-medium w-20">ID</th>
                                    <th className="px-4 py-4 font-medium w-48">상품명</th>
                                    <th className="px-4 py-4 font-medium w-28">평점</th>
                                    <th className="px-4 py-4 font-medium">리뷰 내용</th>
                                    <th className="px-4 py-4 font-medium w-32">작성자</th>
                                    <th className="px-4 py-4 font-medium w-32">작성일</th>
                                    {/* 💡 상태 컬럼 추가 */}
                                    <th className="px-4 py-4 font-medium w-24 text-center">상태</th>
                                    <th className="px-4 py-4 font-medium w-24 text-center">관리</th>
                                </tr>
                                </thead>
                                <tbody>
                                {loading ? (
                                    <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">조회 중...</td></tr>
                                ) : reviews.length === 0 ? (
                                    <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">등록된 리뷰가 없습니다.</td></tr>
                                ) : (
                                    reviews.map((review) => (
                                        <tr
                                            key={review.reviewId}
                                            className={`border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer ${review.isDeleted === 'Y' ? 'bg-slate-50 opacity-60' : ''}`}
                                            onClick={() => setSelectedReview(review)}
                                        >
                                            <td className="px-4 py-4 text-slate-500">{review.reviewId}</td>
                                            <td className="px-4 py-4 font-medium text-slate-900 truncate max-w-[200px]" title={review.productName}>
                                                {review.productName}
                                            </td>
                                            <td className="px-4 py-4">{renderStars(review.rating)}</td>
                                            <td className="px-4 py-4">
                                                <p className="text-slate-700 line-clamp-2" title={review.content}>
                                                    {review.content}
                                                </p>
                                            </td>
                                            <td className="px-4 py-4">{review.reviewerName || review.memberId}</td>
                                            <td className="px-4 py-4 text-slate-500">
                                                {review.createdAt ? review.createdAt.substring(0, 10) : "-"}
                                            </td>
                                            <td className="px-4 py-4 text-center">
                                                {/* 💡 상태 뱃지 표시 */}
                                                {review.isDeleted === 'Y' ? (
                                                    <span className="rounded-full bg-slate-200 px-2 py-1 text-xs font-semibold text-slate-600">숨김됨</span>
                                                ) : (
                                                    <span className="rounded-full bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-600">노출중</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-4 text-center">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-red-500 hover:text-red-700 hover:bg-red-50 disabled:opacity-50"
                                                    disabled={review.isDeleted === 'Y'}
                                                    onClick={(e) => handleDelete(e, review.reviewId, review.isDeleted)}
                                                >
                                                    숨김
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {selectedReview && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSelectedReview(null)}>
                    <div className="w-full max-w-lg rounded-[28px] bg-white shadow-xl flex flex-col max-h-[85vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-6 border-b border-slate-100">
                            <div className="flex items-center gap-3">
                                <h2 className="text-xl font-bold">리뷰 상세</h2>
                                {/* 💡 모달 헤더에도 상태 뱃지 추가 */}
                                {selectedReview.isDeleted === 'Y' && <span className="rounded-full bg-slate-200 px-2 py-1 text-xs font-semibold text-slate-600">숨김 처리된 리뷰</span>}
                            </div>
                            <button className="text-slate-400 hover:text-slate-700 text-3xl font-light leading-none" onClick={() => setSelectedReview(null)}>&times;</button>
                        </div>

                        <div className="p-6 overflow-y-auto space-y-5">
                            <div>
                                <p className="text-sm font-semibold text-slate-500 mb-1">상품명</p>
                                <p className="font-bold text-slate-900 text-lg">{selectedReview.productName}</p>
                            </div>

                            <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                                <div><p className="text-sm font-semibold text-slate-500 mb-1">작성자</p><p className="font-medium text-slate-900">{selectedReview.reviewerName || selectedReview.memberId}</p></div>
                                <div><p className="text-sm font-semibold text-slate-500 mb-1">작성일</p><p className="text-sm text-slate-900">{selectedReview.createdAt ? selectedReview.createdAt.replace("T", " ") : "-"}</p></div>
                                <div className="col-span-2 pt-2 border-t border-slate-200/60 mt-2"><p className="text-sm font-semibold text-slate-500 mb-1">평점</p><div className="text-lg">{renderStars(selectedReview.rating)}</div></div>
                            </div>

                            <div>
                                <p className="text-sm font-semibold text-slate-500 mb-2">리뷰 내용</p>
                                <div className="bg-white border border-slate-200 rounded-2xl p-5 text-slate-800 leading-relaxed whitespace-pre-wrap">
                                    {selectedReview.content}
                                </div>
                            </div>
                        </div>

                        <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3">
                            <Button variant="outline" className="rounded-xl" onClick={() => setSelectedReview(null)}>닫기</Button>
                            {/* 💡 숨김 상태가 아닐 때만 버튼 활성화 */}
                            {selectedReview.isDeleted !== 'Y' && (
                                <Button
                                    variant="destructive"
                                    className="rounded-xl bg-slate-800 hover:bg-slate-900"
                                    onClick={(e) => handleDelete(e, selectedReview.reviewId, selectedReview.isDeleted)}
                                >
                                    리뷰 숨김 처리
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    )
}